import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import BASE_URL from '../../../config/config';
import Loader from '../../../components/Loader';
import Popup from '../../../components/Popup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    FiPlus, FiX, FiTrash2, FiSearch, FiRefreshCw, FiEdit2, FiClock,
    FiInbox, FiUsers, FiLayers, FiArrowUp, FiArrowDown, FiCheck,
} from 'react-icons/fi';
import { CATEGORIAS, CATEGORIA_META } from './servicios/categorias';

const FILA_VACIA = () => ({ id: `n-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, texto: '' });

const ServiciosManager = () => {
    const [cargando, setCargando] = useState(true);
    const [refrescando, setRefrescando] = useState(false);
    const [servicios, setServicios] = useState([]);

    // Filtros
    const [busqueda, setBusqueda] = useState('');
    const [filtroCategoria, setFiltroCategoria] = useState('todas');
    const [orden, setOrden] = useState('asc');

    // Formulario (alta y edición comparten modal)
    const [mostrarFormulario, setMostrarFormulario] = useState(false);
    const [servicioEditando, setServicioEditando] = useState(null);
    const [titulo, setTitulo] = useState('');
    const [categoria, setCategoria] = useState('desarrollo');
    const [horas, setHoras] = useState('');
    const [puntos, setPuntos] = useState([FILA_VACIA()]);
    const [guardando, setGuardando] = useState(false);

    const [servicioAEliminar, setServicioAEliminar] = useState(null);

    const token = localStorage.getItem('AuthToken');
    const tokenInfo = token ? jwtDecode(token) : {};
    const navigate = useNavigate();

    useEffect(() => {
        if (tokenInfo.role !== 'admin') {
            navigate('/dashboard');
            return;
        }
        cargarServicios();
        // eslint-disable-next-line
    }, []);

    const cargarServicios = async (silencioso = false) => {
        if (silencioso) setRefrescando(true);
        try {
            const resp = await axios.get(`${BASE_URL}/servicios`, { headers: { Authorization: token } });
            if (resp.data.success) setServicios(resp.data.servicios);
        } catch (error) {
            console.error('Error al cargar servicios:', error);
            toast.error('Error al cargar los servicios');
        } finally {
            setCargando(false);
            setRefrescando(false);
        }
    };

    const serviciosFiltrados = useMemo(() => {
        let resultado = [...servicios];

        if (filtroCategoria !== 'todas') {
            resultado = resultado.filter(s => s.categoria === filtroCategoria);
        }

        if (busqueda.trim()) {
            const texto = busqueda.toLowerCase();
            resultado = resultado.filter(s =>
                s.titulo?.toLowerCase().includes(texto) ||
                (s.descripcion || []).some(p => p.toLowerCase().includes(texto))
            );
        }

        resultado.sort((a, b) => {
            const diff = a.titulo.localeCompare(b.titulo, 'es');
            return orden === 'asc' ? diff : -diff;
        });

        return resultado;
    }, [servicios, filtroCategoria, busqueda, orden]);

    const hayFiltrosActivos = filtroCategoria !== 'todas' || busqueda.trim() !== '';

    const limpiarFiltros = () => {
        setFiltroCategoria('todas');
        setBusqueda('');
    };

    const abrirNuevo = () => {
        setServicioEditando(null);
        setTitulo('');
        setCategoria('desarrollo');
        setHoras('');
        setPuntos([FILA_VACIA()]);
        setMostrarFormulario(true);
    };

    const abrirEdicion = (servicio) => {
        setServicioEditando(servicio);
        setTitulo(servicio.titulo);
        setCategoria(servicio.categoria);
        setHoras(servicio.horas === null || typeof servicio.horas === 'undefined' ? '' : String(servicio.horas));
        setPuntos(
            (servicio.descripcion || []).length > 0
                ? servicio.descripcion.map((texto, i) => ({ id: `p-${servicio.id}-${i}`, texto }))
                : [FILA_VACIA()]
        );
        setMostrarFormulario(true);
    };

    const cerrarFormulario = () => {
        setMostrarFormulario(false);
        setServicioEditando(null);
    };

    // ── Descripción: lista ampliable ──────────────────────────────────────
    const cambiarPunto = (id, texto) => {
        setPuntos(prev => prev.map(p => (p.id === id ? { ...p, texto } : p)));
    };

    const anadirPunto = () => setPuntos(prev => [...prev, FILA_VACIA()]);

    const quitarPunto = (id) => {
        setPuntos(prev => (prev.length === 1 ? [FILA_VACIA()] : prev.filter(p => p.id !== id)));
    };

    // Enter dentro de un punto crea el siguiente, como en una lista real
    const teclaEnPunto = (e, indice) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        const nueva = FILA_VACIA();
        setPuntos(prev => [...prev.slice(0, indice + 1), nueva, ...prev.slice(indice + 1)]);
        // El foco se lleva al input recién creado en cuanto React lo pinta
        setTimeout(() => {
            const campo = document.querySelector(`[data-punto="${nueva.id}"]`);
            if (campo) campo.focus();
        }, 0);
    };

    const guardar = async (e) => {
        e.preventDefault();

        if (!titulo.trim()) {
            toast.error('El título es obligatorio');
            return;
        }
        if (horas !== '' && (Number.isNaN(Number(horas)) || Number(horas) < 0)) {
            toast.error('Las horas deben ser un número positivo');
            return;
        }

        setGuardando(true);
        try {
            const datos = {
                titulo: titulo.trim(),
                categoria,
                horas: horas === '' ? null : Number(horas),
                descripcion: puntos.map(p => p.texto.trim()).filter(Boolean),
            };

            const resp = servicioEditando
                ? await axios.put(`${BASE_URL}/servicios/${servicioEditando.id}`, datos, { headers: { Authorization: token } })
                : await axios.post(`${BASE_URL}/servicios`, datos, { headers: { Authorization: token } });

            if (resp.data.success) {
                toast.success(resp.data.message);
                cerrarFormulario();
                cargarServicios();
            }
        } catch (error) {
            console.error('Error al guardar servicio:', error);
            toast.error(error.response?.data?.message || 'Error al guardar el servicio');
        } finally {
            setGuardando(false);
        }
    };

    const eliminarServicio = async () => {
        if (!servicioAEliminar) return;
        try {
            const resp = await axios.delete(`${BASE_URL}/servicios/${servicioAEliminar.id}`, {
                headers: { Authorization: token },
            });
            if (resp.data.success) {
                toast.success('Servicio eliminado');
                cargarServicios();
            }
        } catch (error) {
            console.error('Error al eliminar servicio:', error);
            toast.error('Error al eliminar el servicio');
        } finally {
            setServicioAEliminar(null);
        }
    };

    const resumen = useMemo(() => ({
        total: servicios.length,
        asignados: servicios.reduce((acc, s) => acc + (s.usuarios_asignados || 0), 0),
        horas: servicios.reduce((acc, s) => acc + (s.horas || 0), 0),
    }), [servicios]);

    if (cargando) return <div className="loader-fullscreen"><Loader /></div>;

    return (
        <main className="site-width">
            <ToastContainer position="bottom-right" autoClose={3000} />

            <div className="gn-page">
                <button className="boton-volver" onClick={() => navigate('/dashboard')}>
                    ← Volver
                </button>

                <div className="gn-header">
                    <div className="pagina-cabecera">
                        <h2>Gestor de servicios</h2>
                        <p>Da de alta el catálogo de servicios y asígnalos después a cada cliente.</p>
                    </div>
                    <div className="gn-header-acciones">
                        <button
                            className="gn-boton-icono"
                            onClick={() => cargarServicios(true)}
                            disabled={refrescando}
                            title="Actualizar"
                        >
                            <FiRefreshCw className={refrescando ? 'gn-girando' : ''} />
                        </button>
                        <button className="boton-primario gn-boton-nueva" onClick={abrirNuevo}>
                            <FiPlus /> Nuevo servicio
                        </button>
                    </div>
                </div>

                <div className="gn-stats">
                    <div className="gn-stat-card">
                        <span className="gn-stat-icono"><FiLayers /></span>
                        <div>
                            <p className="gn-stat-valor">{resumen.total}</p>
                            <p className="gn-stat-label">Servicios en catálogo</p>
                            <p className="gn-stat-sub">Disponibles para asignar</p>
                        </div>
                    </div>
                    <div className="gn-stat-card">
                        <span className="gn-stat-icono"><FiUsers /></span>
                        <div>
                            <p className="gn-stat-valor">{resumen.asignados}</p>
                            <p className="gn-stat-label">Asignaciones activas</p>
                            <p className="gn-stat-sub">Servicios vinculados a clientes</p>
                        </div>
                    </div>
                    <div className="gn-stat-card">
                        <span className="gn-stat-icono"><FiClock /></span>
                        <div>
                            <p className="gn-stat-valor">{resumen.horas}</p>
                            <p className="gn-stat-label">Horas del catálogo</p>
                            <p className="gn-stat-sub">Suma de los servicios con horas</p>
                        </div>
                    </div>
                </div>

                <div className="gn-toolbar">
                    <div className="gn-campo-icono gn-buscador">
                        <FiSearch className="gn-icono-input" />
                        <input
                            type="text"
                            placeholder="Buscar por título o descripción..."
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                            className="campo-busqueda gn-input-con-icono"
                        />
                    </div>
                    <select value={filtroCategoria} onChange={(e) => setFiltroCategoria(e.target.value)} className="selector">
                        <option value="todas">Todas las categorías</option>
                        {CATEGORIAS.map(valor => (
                            <option key={valor} value={valor}>{CATEGORIA_META[valor].label}</option>
                        ))}
                    </select>
                    <button
                        className="gn-boton-icono"
                        onClick={() => setOrden(o => (o === 'asc' ? 'desc' : 'asc'))}
                        title={orden === 'asc' ? 'A-Z' : 'Z-A'}
                    >
                        {orden === 'asc' ? <FiArrowDown /> : <FiArrowUp />}
                    </button>
                </div>

                <div className="gn-lista">
                    <div className="gn-lista-cabecera">
                        <p className="info-resultados">
                            Mostrando {serviciosFiltrados.length} de {servicios.length} servicios
                        </p>
                    </div>

                    {serviciosFiltrados.length > 0 ? (
                        <div className="srv-tabla">
                            <div className="srv-tabla-cabecera">
                                <span>Servicio</span>
                                <span>Categoría</span>
                                <span>Horas</span>
                                <span>Clientes</span>
                                <span className="srv-col-acciones">Acciones</span>
                            </div>

                            {serviciosFiltrados.map((servicio) => {
                                const meta = CATEGORIA_META[servicio.categoria] || CATEGORIA_META.desarrollo;
                                return (
                                    <div key={servicio.id} className="srv-fila">
                                        <div className="srv-celda-servicio">
                                            <h4>{servicio.titulo}</h4>
                                            {servicio.descripcion?.length > 0 ? (
                                                <ul className="srv-lista-puntos">
                                                    {servicio.descripcion.map((punto, i) => (
                                                        <li key={i}>{punto}</li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <p className="srv-sin-descripcion">Sin descripción</p>
                                            )}
                                        </div>

                                        <div className="srv-celda">
                                            <span className={`srv-categoria srv-categoria--${servicio.categoria}`}>
                                                {meta.label}
                                            </span>
                                        </div>

                                        <div className="srv-celda">
                                            {servicio.horas !== null && typeof servicio.horas !== 'undefined' ? (
                                                <span className="srv-horas"><FiClock /> {servicio.horas} h</span>
                                            ) : (
                                                <span className="srv-vacio-celda">—</span>
                                            )}
                                        </div>

                                        <div className="srv-celda">
                                            <span className="srv-clientes">{servicio.usuarios_asignados || 0}</span>
                                        </div>

                                        <div className="srv-celda srv-col-acciones">
                                            <button
                                                type="button"
                                                className="gn-boton-icono"
                                                onClick={() => abrirEdicion(servicio)}
                                                title="Editar servicio"
                                            >
                                                <FiEdit2 />
                                            </button>
                                            <button
                                                type="button"
                                                className="gn-boton-icono srv-boton-eliminar"
                                                onClick={() => setServicioAEliminar(servicio)}
                                                title="Eliminar servicio"
                                            >
                                                <FiTrash2 />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="gn-vacio">
                            <FiInbox />
                            <p>{servicios.length === 0 ? 'Todavía no hay servicios en el catálogo.' : 'No se encontraron servicios.'}</p>
                            {hayFiltrosActivos ? (
                                <button className="gn-boton-cancelar" onClick={limpiarFiltros}>Limpiar filtros</button>
                            ) : (
                                <button className="boton-primario gn-boton-nueva" onClick={abrirNuevo}>
                                    <FiPlus /> Crear el primero
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {mostrarFormulario && (
                <div className="gn-modal-overlay" onClick={cerrarFormulario}>
                    <div className="gn-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="gn-modal-header">
                            <h3>{servicioEditando ? <><FiEdit2 /> Editar servicio</> : <><FiPlus /> Nuevo servicio</>}</h3>
                            <button type="button" className="gn-boton-cerrar" onClick={cerrarFormulario}>
                                <FiX />
                            </button>
                        </div>

                        <form onSubmit={guardar} className="gn-modal-form">
                            <div className="campo-formulario">
                                <label>Título *</label>
                                <input
                                    type="text"
                                    value={titulo}
                                    onChange={(e) => setTitulo(e.target.value)}
                                    placeholder="Mantenimiento web mensual"
                                    maxLength={150}
                                    required
                                />
                            </div>

                            <div className="campo-formulario">
                                <label>Categoría *</label>
                                <div className="gn-chips">
                                    {CATEGORIAS.map((valor) => {
                                        const { label, icon: Icono } = CATEGORIA_META[valor];
                                        return (
                                            <button
                                                type="button"
                                                key={valor}
                                                className={`gn-chip ${categoria === valor ? 'activo' : ''}`}
                                                onClick={() => setCategoria(valor)}
                                            >
                                                <Icono /> {label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="campo-formulario">
                                <label>Horas (opcional)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={horas}
                                    onChange={(e) => setHoras(e.target.value)}
                                    placeholder="Ej. 10"
                                />
                                <small>Déjalo vacío si el servicio no lleva una bolsa de horas.</small>
                            </div>

                            <div className="campo-formulario">
                                <label>Descripción (opcional)</label>
                                <div className="srv-editor-lista">
                                    {puntos.map((punto, indice) => (
                                        <div key={punto.id} className="srv-editor-fila">
                                            <span className="srv-editor-bullet" aria-hidden="true" />
                                            <input
                                                type="text"
                                                data-punto={punto.id}
                                                value={punto.texto}
                                                onChange={(e) => cambiarPunto(punto.id, e.target.value)}
                                                onKeyDown={(e) => teclaEnPunto(e, indice)}
                                                placeholder="Punto incluido en el servicio"
                                            />
                                            <button
                                                type="button"
                                                className="srv-editor-quitar"
                                                onClick={() => quitarPunto(punto.id)}
                                                title="Quitar punto"
                                            >
                                                <FiX />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <button type="button" className="srv-editor-anadir" onClick={anadirPunto}>
                                    <FiPlus /> Añadir punto
                                </button>
                                <small>Pulsa Enter dentro de un punto para crear el siguiente.</small>
                            </div>

                            <div className="botones-formulario">
                                <button type="submit" className="boton-primario" disabled={guardando}>
                                    <FiCheck /> {guardando ? 'Guardando…' : (servicioEditando ? 'Guardar cambios' : 'Crear servicio')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <Popup
                open={Boolean(servicioAEliminar)}
                title="Eliminar servicio"
                onClose={() => setServicioAEliminar(null)}
                showConfirm={false}
                footerActions={(
                    <button className="boton-aceptar-popup" onClick={eliminarServicio}>Eliminar</button>
                )}
            >
                <p>Vas a eliminar <b>{servicioAEliminar?.titulo}</b>.</p>
                {servicioAEliminar?.usuarios_asignados > 0 && (
                    <p>Se desasignará de {servicioAEliminar.usuarios_asignados} cliente(s).</p>
                )}
                <p>Esta acción no se puede deshacer.</p>
            </Popup>
        </main>
    );
};

export default ServiciosManager;
